import React, { useMemo, useState } from 'react';
import { formatProjectDateOnly } from 'src/utils/date';
import { useProjectSettingCtx } from 'src/features/projects/contexts/ProjectSettingContext';
import MilestoneStatusBadge from './MilestoneStatusBadge';

type UUID = string;

export type ActualPayment = {
    id: UUID;
    milestoneId: UUID;
    amount: any; // Decimal/string/number
    paidAt: string; // YYYY-MM-DD or ISO
    note?: string | null;
};

type CriteriaLine = { id?: string; done?: boolean; text: string };

export type Milestone = {
    id: UUID;
    seq: number;
    label: string;
    amountPlan?: any | null;
    dueDate?: string | null;
    completionCriteria?: any; // string | json
    payments?: ActualPayment[];
};

function toNum(v: any): number {
    if (v == null) return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
}

function toDateOnly(d?: string | null, zone?: string) {
    if (!d) return '—';
    return formatProjectDateOnly(d, zone ?? 'Asia/Seoul', '—');
}

function fmtMoney(n: number, currency?: string | null) {
    const v = Math.round(toNum(n));
    return `${v.toLocaleString()}${currency ? ` ${currency}` : ''}`;
}

function Chevron({ open }: { open: boolean }) {
    return (
        <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
            aria-hidden
        >
            <path d="M7 10l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    );
}

/**
 * completionCriteria가:
 * - 이미 배열(JSON)인 경우: 그대로 lines로 변환
 * - 문자열인 경우: JSON parse 시도 -> 배열이면 변환, 아니면 그냥 한 줄 텍스트
 */
function normalizeCriteriaLines(v: any): CriteriaLine[] {
    if (v == null) return [];

    // 1) 배열이면 바로 변환
    if (Array.isArray(v)) {
        return v
            .map((x) => {
                if (x == null) return null;
                if (typeof x === 'string') return { text: x } as CriteriaLine;

                const text = typeof x.text === 'string' ? x.text : '';
                if (!text.trim()) return null;

                return {
                    id: typeof x.id === 'string' ? x.id : undefined,
                    done: typeof x.done === 'boolean' ? x.done : undefined,
                    text,
                } as CriteriaLine;
            })
            .filter(Boolean) as CriteriaLine[];
    }

    // 2) 문자열이면 JSON parse를 시도
    if (typeof v === 'string') {
        const s = v.trim();
        if (!s) return [];

        // JSON 배열/객체처럼 보이면 파싱 시도
        if ((s.startsWith('[') && s.endsWith(']')) || (s.startsWith('{') && s.endsWith('}'))) {
            try {
                const parsed = JSON.parse(s);
                if (Array.isArray(parsed)) return normalizeCriteriaLines(parsed);
                // 객체 1개면 text로
                if (parsed && typeof parsed === 'object') {
                    const t = typeof parsed.text === 'string' ? parsed.text : s;
                    return t.trim() ? [{ text: t }] : [];
                }
            } catch {
                // parse 실패 -> 그냥 string 한 줄로
            }
        }
        return [{ text: s }];
    }

    // 3) 그 외 타입은 문자열화
    try {
        const s = JSON.stringify(v);
        return s ? [{ text: s }] : [];
    } catch {
        const s = String(v);
        return s ? [{ text: s }] : [];
    }
}

export default function PaymentMilestoneList(props: {
    currency: string | null;
    milestones: Milestone[];
    onAddPayment: (m: { id: string; label: string }) => void;
    onEditPayment: (m: { id: string; label: string }, p: ActualPayment) => void;
    onDeletePayment: (paymentId: string) => void;
    canEdit?: boolean;
}) {
    const { currency, milestones, onAddPayment, onEditPayment, onDeletePayment, canEdit } = props;
    const { setting } = useProjectSettingCtx();
    const zone = setting?.timeZone ?? 'Asia/Seoul';

    // ✅ expanded: partial payments만 펼침(Actual 컬럼)
    const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

    const rows = useMemo(() => {
        return (milestones ?? [])
            .slice()
            .sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0))
            .map((m) => {
                const plan = toNum(m.amountPlan);
                const payments = Array.isArray(m.payments) ? m.payments : [];
                const actual = payments.reduce((s, p) => s + toNum(p.amount), 0);
                const remaining = plan - actual;

                const lines = normalizeCriteriaLines((m as any).completionCriteria);

                const partial = payments
                    .slice()
                    .sort((a, b) => String(a.paidAt ?? '').localeCompare(String(b.paidAt ?? '')))
                    .map((p) => ({
                        id: p.id,
                        date: formatProjectDateOnly(p.paidAt, zone, String(p.paidAt ?? '')),
                        amountText: fmtMoney(toNum(p.amount), currency),
                        raw: p,
                    }));

                return { m, plan, actual, remaining, lines, partial };
            });
    }, [milestones, currency, zone]);

    const togglePartial = (id: string) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    if (!rows.length) {
        return (
            <div className="text-sm text-gray-500">
                마일스톤이 없습니다. 계약관리(Contract)에서 마일스톤을 먼저 생성하세요.
            </div>
        );
    }

    return (
        <div className="mt-3 space-y-2">
            {/* Header (desktop) */}
            <div className="hidden md:grid grid-cols-12 gap-2 px-2 text-[11px] text-gray-500">
                <div className="col-span-2">Due Date</div>
                <div className="col-span-4">Completion Criteria</div>
                <div className="col-span-2 text-right">Plan</div>
                <div className="col-span-2 text-right">Actual</div>
                <div className="col-span-1 text-right">Remaining</div>
                <div className="col-span-1 text-right">Status</div>
            </div>

            {rows.map(({ m, plan, actual, remaining, lines, partial }) => {
                const isOpen = expanded.has(m.id);

                // clamp 여부: 4줄 넘어갈 가능성이 있으면 그라데이션/… 표시
                // (정확히 측정하려면 DOM measurement가 필요하지만, 여기선 단순 기준)
                const needsClamp = lines.length > 4;

                return (
                    <div key={m.id} className="rounded-xl border bg-white overflow-hidden">
                        <div className="px-3 py-3">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <div className="text-sm font-medium min-w-0 truncate">
                                            {m.seq}. {m.label}
                                        </div>
                                        <div className="hidden md:block">
                                            <MilestoneStatusBadge dueDate={m.dueDate} plan={plan} actual={actual} />
                                        </div>
                                    </div>

                                    {/* mobile */}
                                    <div className="md:hidden mt-1 text-xs text-gray-600 space-y-1">
                                        <div className="flex items-center justify-between">
                                            <span>Due: {toDateOnly(m.dueDate, zone)}</span>
                                            <MilestoneStatusBadge dueDate={m.dueDate} plan={plan} actual={actual} />
                                        </div>

                                        {/* ✅ Criteria는 그냥 표시 (MilestonesList 스타일 간소화) */}
                                        <div className="relative">
                                            <ul className="list-disc pl-4 space-y-1 line-clamp-4 overflow-hidden [display:-webkit-box] [-webkit-box-orient:vertical]">
                                                {lines.map((c, i) => (
                                                    <li key={c.id ?? i} className={c.done ? 'line-through text-gray-500' : ''}>
                                                        {c.text}
                                                    </li>
                                                ))}
                                                {lines.length === 0 && <li className="text-gray-400">—</li>}
                                            </ul>

                                            {needsClamp && (
                                                <>
                                                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-6 bg-gradient-to-t from-white to-transparent" />
                                                    <span className="pointer-events-none absolute right-1 bottom-0.5 text-xs text-gray-400 select-none">
                                                        …
                                                    </span>
                                                </>
                                            )}
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <span>Plan: {fmtMoney(plan, currency)}</span>

                                            {/* ✅ partial 토글은 Actual 클릭 */}
                                            <button
                                                type="button"
                                                className="inline-flex items-center gap-1 hover:underline"
                                                onClick={() => togglePartial(m.id)}
                                                title={isOpen ? 'partial 닫기' : 'partial 보기'}
                                            >
                                                <span>Actual: {fmtMoney(actual, currency)}</span>
                                                <span className="text-gray-400">
                                                    <Chevron open={isOpen} />
                                                </span>
                                            </button>

                                            <span>Rem: {fmtMoney(remaining, currency)}</span>
                                        </div>

                                        {/* ✅ mobile partial stack */}
                                        <div
                                            className={[
                                                'overflow-hidden transition-[max-height,opacity] duration-300 ease-out',
                                                isOpen ? 'max-h-[420px] opacity-100' : 'max-h-0 opacity-0',
                                            ].join(' ')}
                                        >
                                            <div className="pt-2">
                                                <div className="text-[11px] text-gray-500 mb-1">Partial Payments</div>

                                                {partial.length ? (
                                                    <div className="space-y-1">
                                                        {partial.map((pp) => (
                                                            <div
                                                                key={pp.id}
                                                                className="flex items-center justify-between rounded-md border px-2 py-1.5"
                                                            >
                                                                <div className="text-xs text-gray-800">
                                                                    [{pp.date || '—'}] {pp.amountText}
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    <button
                                                                        type="button"
                                                                        className="text-[11px] text-gray-700 hover:underline"
                                                                        onClick={() => onEditPayment({ id: m.id, label: m.label }, pp.raw)}
                                                                    >
                                                                        Edit
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="text-[11px] text-rose-600 hover:underline"
                                                                        onClick={() => {
                                                                            // eslint-disable-next-line no-restricted-globals
                                                                            if (!confirm('이 지급/수령 이벤트를 삭제할까요?')) return;
                                                                            onDeletePayment(pp.id);
                                                                        }}
                                                                    >
                                                                        Del
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="text-sm text-gray-500">No partial payments</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* desktop grid */}
                                    <div className="hidden md:grid grid-cols-12 gap-2 mt-2 text-xs text-gray-700">
                                        <div className="col-span-2">{toDateOnly(m.dueDate)}</div>

                                        {/* ✅ Criteria 컬럼: MilestonesList 스타일로 그대로 */}
                                        <div className="col-span-4 min-w-0">
                                            <div className="relative">
                                                <ul className="list-disc pl-4 space-y-1 line-clamp-4 overflow-hidden [display:-webkit-box] [-webkit-box-orient:vertical]">
                                                    {lines.map((c, i) => (
                                                        <li
                                                            key={c.id ?? i}
                                                            className={c.done ? 'line-through text-gray-500' : ''}
                                                        >
                                                            {c.text}
                                                        </li>
                                                    ))}
                                                    {lines.length === 0 && <li className="text-gray-400">—</li>}
                                                </ul>

                                                {needsClamp && (
                                                    <>
                                                        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-6 bg-gradient-to-t from-white to-transparent" />
                                                        <span className="pointer-events-none absolute right-1 bottom-0.5 text-xs text-gray-400 select-none">
                                                            …
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        <div className="col-span-2 text-right tabular-nums">{fmtMoney(plan, currency)}</div>

                                        {/* ✅ Actual: 합계만 기본, 클릭하면 동일 셀 아래로 partial stack */}
                                        <div className="col-span-2 text-right tabular-nums">
                                            <div className="inline-flex flex-col items-end">
                                                <button
                                                    type="button"
                                                    className="inline-flex items-center gap-1 hover:underline"
                                                    onClick={() => togglePartial(m.id)}
                                                    title={isOpen ? 'partial 닫기' : 'partial 보기'}
                                                >
                                                    <span>{fmtMoney(actual, currency)}</span>
                                                    <span className="text-gray-400">
                                                        <Chevron open={isOpen} />
                                                    </span>
                                                </button>

                                                <div
                                                    className={[
                                                        'w-full overflow-hidden transition-[max-height,opacity] duration-300 ease-out',
                                                        isOpen ? 'max-h-[240px] opacity-100' : 'max-h-0 opacity-0',
                                                    ].join(' ')}
                                                >
                                                    <div className="mt-2 w-[240px] max-w-full rounded-lg border bg-gray-50 p-2 text-left">
                                                        {partial.length ? (
                                                            <div className="space-y-1">
                                                                {partial.map((pp) => (
                                                                    <div
                                                                        key={pp.id}
                                                                        className="flex items-center justify-between gap-2"
                                                                    >
                                                                        <div className="text-[11px] text-gray-700">
                                                                            [{pp.date || '—'}] {pp.amountText}
                                                                        </div>
                                                                        <div className="flex items-center gap-2 shrink-0">
                                                                            <button
                                                                                type="button"
                                                                                className="text-[11px] text-gray-700 hover:underline disabled:underline-none"
                                                                                onClick={() => onEditPayment({ id: m.id, label: m.label }, pp.raw)}
                                                                                disabled={!canEdit}
                                                                                title={!canEdit ? '권한이 없습니다.' : undefined}
                                                                            >
                                                                                Edit
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className="text-[11px] text-rose-600 hover:underline disabled:underline-none"
                                                                                onClick={() => {
                                                                                    // eslint-disable-next-line no-restricted-globals
                                                                                    if (!confirm('이 지급/수령 이벤트를 삭제할까요?')) return;
                                                                                    onDeletePayment(pp.id);
                                                                                }}
                                                                                disabled={!canEdit}
                                                                                title={!canEdit ? '권한이 없습니다.' : undefined}
                                                                            >
                                                                                Del
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <div className="text-[11px] text-gray-500">No partial payments</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-span-1 text-right tabular-nums">{fmtMoney(remaining, currency)}</div>

                                        <div className="col-span-1 text-right">
                                            <MilestoneStatusBadge dueDate={m.dueDate} plan={plan} actual={actual} />
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2 shrink-0">
                                    <button
                                        type="button"
                                        className="text-xs rounded-lg border px-2.5 py-1.5 bg-white hover:bg-gray-50 disabled:opacity-50"
                                        onClick={() => onAddPayment({ id: m.id, label: m.label })}
                                        disabled={!canEdit}
                                        title={!canEdit ? '권한이 없습니다.' : undefined}
                                    >
                                        + Payment
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
